import React, { useState, useEffect } from 'react';

const SessionToast = ({ message, type = 'success', duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          border: 'border-green-400/50',
          bg: 'bg-green-900/60',
          text: 'text-green-300',
          shadow: 'shadow-neon-green',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          )
        };
      case 'warning':
        return {
          border: 'border-yellow-400/50',
          bg: 'bg-yellow-900/60',
          text: 'text-yellow-300',
          shadow: 'shadow-neon-yellow',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
            </svg>
          )
        };
      case 'error':
        return {
          border: 'border-red-400/50',
          bg: 'bg-red-900/60',
          text: 'text-red-300',
          shadow: 'shadow-neon-pink',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          )
        };
      default:
        return {
          border: 'border-cyan-400/50',
          bg: 'bg-cyan-900/60',
          text: 'text-cyan-300',
          shadow: 'shadow-neon-cyan',
          icon: (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 9h-2V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v2H1l4 4 4-4H7V7h4v2z"/>
            </svg>
          )
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
    }`}>
      <div className={`${styles.bg} border ${styles.border} rounded-xl p-4 ${styles.shadow} backdrop-blur-md max-w-sm relative overflow-hidden`}>
        {/* Cyberpunk decorative elements */}
        <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-current to-transparent animate-slide-right ${styles.text}`}></div>
        <div className={`absolute -top-10 -right-10 w-20 h-20 ${styles.bg} rounded-full blur-lg opacity-50`}></div>

        {/* Toast Content */}
        <div className="relative z-10 flex items-start gap-3">
          <div className={`w-8 h-8 bg-black/60 border ${styles.border} rounded-lg flex items-center justify-center ${styles.shadow}`}>
            <div className={styles.text}>
              {styles.icon}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <p className={`font-mono font-bold text-sm ${styles.text} leading-tight`}>
              {message}
            </p>
          </div>

          <button
            onClick={handleClose}
            className={`w-6 h-6 ${styles.text} hover:opacity-80 transition-opacity duration-200 flex-shrink-0`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }

        .animate-slide-right { 
          animation: slide-right 3s ease-in-out infinite; 
        }

        .shadow-neon-green { 
          box-shadow: 0 0 20px rgba(0,255,128,0.5), 0 0 40px rgba(0,255,128,0.3), 0 0 60px rgba(0,255,128,0.1); 
        }
        
        .shadow-neon-cyan { 
          box-shadow: 0 0 20px rgba(0,255,255,0.5), 0 0 40px rgba(0,255,255,0.3), 0 0 60px rgba(0,255,255,0.1); 
        }
        
        .shadow-neon-yellow { 
          box-shadow: 0 0 20px rgba(255,255,0,0.5), 0 0 40px rgba(255,255,0,0.3), 0 0 60px rgba(255,255,0,0.1); 
        }
        
        .shadow-neon-pink { 
          box-shadow: 0 0 20px rgba(255,0,128,0.5), 0 0 40px rgba(255,0,128,0.3), 0 0 60px rgba(255,0,128,0.1); 
        }
      `}</style>
    </div>
  );
};

export default SessionToast; 